import * as ROUTES from '../../domain/routes';
import { BenefitsListPage } from '@/@types/benefitsListPage';
import { Benefit } from '@/@types/benefits';
import { BenefitDefinition } from '@/@types/benefit';
import { InsuranceActivationStatus, InsurancePackageType } from '@/@types/insurance';

type BenefitState = {
  isRealEstateProofRequested: boolean;
  isRealEstateDamageReportRequested: boolean;
  hasMieterPlus: boolean;
  hasWohnenPlus: boolean;
  hasKaeuferPlus: boolean;
  canVisitDeposit: boolean;
  isEntitledForTenantProtection: boolean;
  isEntitledForRelocationBox: boolean;
  isEntitledForKeyEmergency: boolean;
  isRechtsberatungEntitled: boolean;
  isMietrechtsschutzEntitled: boolean;
  isMediationEntitled: boolean;
  isSchluesseldienstEntitled: boolean;
  isKaeuferPlusLegalInsuranceAvailable: boolean;
  insurancePackageStatus: InsuranceActivationStatus;
  insurancePackageType: InsurancePackageType;
  isWP2410: boolean;
  isKeyAndLegalAvailable: boolean;
  isAutoContactAgentEntitled: boolean;
};

export type BenefitConfig = Omit<BenefitDefinition, 'isEntitledForService' | 'title'> & {
  isEntitledForService?: (state: BenefitState) => boolean;
  shouldInclude?: (state: BenefitState) => boolean;
  title: string | ((state: BenefitState) => string);
};

export const BENEFIT_CONFIGS: Record<BenefitsListPage, BenefitConfig[]> = {
  [BenefitsListPage.MieterPlus]: [
    {
      title: 'benefits.opportunityCheck.headline',
      defaultTitle: 'Opportunity check',
      type: Benefit.POSSIBILITY_CHECK,
      redirectTo: ROUTES.CHANCEN_CHECK,
      isEntitledForService: (state) => state.hasMieterPlus,
    },
    {
      title: 'benefits.exclusiveListings.headline',
      defaultTitle: 'Exclusive listings',
      type: Benefit.ADVERTISEMENTS,
      redirectTo: ROUTES.EXKLUSIVE_ANZEIGEN,
      isEntitledForService: (state) => state.hasMieterPlus,
    },
    {
      title: 'benefits.firstInInbox.headline',
      defaultTitle: 'First in inbox',
      type: Benefit.MESSENGER,
      redirectTo: ROUTES.OBEN_IM_POSTFACH,
      isEntitledForService: (state) => state.hasMieterPlus,
    },
    {
      title: 'benefits.autoContactAgent.headline',
      defaultTitle: 'Bewerbungsassistent',
      type: Benefit.AUTO_CONTACT_AGENT,
      redirectTo: ROUTES.BEWERBUNGSASSISTENT,
      isEntitledForService: (state) => state.isAutoContactAgentEntitled,
    },
    {
      title: 'benefits.consumerBenefitSchufa.headline',
      defaultTitle: 'SCHUFA check',
      type: Benefit.SCHUFA,
      redirectTo: ROUTES.SCHUFA,
    },
    {
      title: (state) =>
        state.hasMieterPlus
          ? 'benefits.documents.headlines.applicantFolder'
          : 'benefits.documents.headlines.profileAndDocuments',
      defaultTitle: 'Documents',
      type: Benefit.DOCUMENTS,
      redirectTo: ROUTES.BEWERBERMAPPE,
    },
    {
      title: 'benefits.relocationService.headline',
      defaultTitle: 'Relocation Service',
      type: Benefit.RELOCATION_SERVICE,
      redirectTo: ROUTES.UMZUGSSERVICE,
    },
    {
      title: 'benefits.addressChange.headline',
      defaultTitle: 'Address Change',
      type: Benefit.ADDRESS_CHANGE,
      redirectTo: ROUTES.ADRESSWECHSEL,
    },
    {
      title: 'benefits.magentaTv.headline',
      defaultTitle: 'MagentaTV Vorteil',
      type: Benefit.MAGENTA_TV,
      redirectTo: ROUTES.MAGENTA_TV,
    },
    {
      title: 'benefits.mieterNetzwerk.headline',
      defaultTitle: 'MieterNetzwerk Vorteil',
      type: Benefit.MIETER_NETZWERK,
      redirectTo: ROUTES.MIETER_NETZWERK,
    },
    {
      title: 'benefits.warteliste.headline',
      defaultTitle: 'Warteliste Vorteil',
      type: Benefit.WARTELISTE,
      redirectTo: ROUTES.WARTELISTE,
    },
  ],
  [BenefitsListPage.WohnenPlus]: [
    {
      title: 'benefits.deposit.headline',
      defaultTitle: 'Deposit',
      type: Benefit.DEPOSIT,
      redirectTo: ROUTES.MIETKAUTIONSBURGSCHAFT,
      isEntitledForService: (state) => state.canVisitDeposit,
      shouldInclude: (state) => state.hasMieterPlus && state.canVisitDeposit,
    },
    {
      title: 'benefits.rentLegal.headline',
      defaultTitle: 'Legal Rent',
      type: Benefit.RENT_LEGAL,
      redirectTo: ROUTES.MIETRECHTSBERATUNG,
      isEntitledForService: (state) => state.isEntitledForKeyEmergency,
      shouldInclude: (state) => !state.isWP2410 && state.isKeyAndLegalAvailable,
    },
    {
      title: 'benefits.relocationBox.headline',
      defaultTitle: 'Relocation Box',
      type: Benefit.RELOCATION_BOX,
      redirectTo: ROUTES.UMZUGSKISTEN,
      shouldInclude: (state) => state.isEntitledForRelocationBox,
    },
    {
      title: 'benefits.keyEmergency.headline',
      defaultTitle: 'Key Emergency',
      type: Benefit.KEY_EMERGENCY,
      redirectTo: ROUTES.SCHLUSSELDIENST,
      isEntitledForService: (state) => state.isEntitledForKeyEmergency,
      shouldInclude: (state) => !state.isWP2410 && state.isKeyAndLegalAvailable,
    },
    {
      title: 'benefits.rechtsberatung.headline',
      defaultTitle: 'Rechtsberatung',
      type: Benefit.RECHTSBERATUNG,
      redirectTo: ROUTES.RECHTSBERATUNG,
      isEntitledForService: (state) => state.isRechtsberatungEntitled,
      shouldInclude: (state) => !state.isEntitledForKeyEmergency || state.isRechtsberatungEntitled,
    },
    {
      title: 'benefits.schluesseldienst.headline',
      defaultTitle: 'schluesseldienst',
      type: Benefit.SCHLUESSELDIENST,
      redirectTo: ROUTES.SCHLUESSELDIENST,
      isEntitledForService: (state) => state.isSchluesseldienstEntitled,
      shouldInclude: (state) => !state.isEntitledForKeyEmergency || state.isSchluesseldienstEntitled,
    },
    {
      title: 'benefits.mietrechtsschutz.headline',
      defaultTitle: 'Mietrechtsschutz',
      type: Benefit.MIETRECHTSSCHUTZ,
      redirectTo: ROUTES.MIETRECHTSSCHUTZ,
      isEntitledForService: (state) => state.isMietrechtsschutzEntitled,
      shouldInclude: (state) => !state.isEntitledForKeyEmergency && state.insurancePackageType !== 'S',
    },
    {
      title: 'benefits.mediation.headline',
      defaultTitle: 'Mediation',
      type: Benefit.MEDIATION,
      redirectTo: ROUTES.MEDIATION,
      isEntitledForService: (state) => state.isMediationEntitled,
      shouldInclude: (state) => !state.isEntitledForKeyEmergency || state.isMediationEntitled,
    },
    {
      title: 'benefits.billCheckV3.headline',
      defaultTitle: 'Bill Check',
      type: Benefit.BILLCHECK,
      redirectTo: ROUTES.NEBENKOSTEN_CHECK,
    },
    {
      title: 'benefits.storageService.headline',
      defaultTitle: 'Storage Service',
      type: Benefit.STORAGE_SERVICE,
      redirectTo: ROUTES.LAGERRAUM,
    },
    {
      title: 'benefits.tenantProtectionServices.headline',
      defaultTitle: 'Tenant Protection Services',
      type: Benefit.TENANT_PROTECTION_SERVICE,
      redirectTo: ROUTES.MIETERSCHUTZ,
      isEntitledForService: (state) => state.isEntitledForTenantProtection,
      shouldInclude: (state) => state.isEntitledForTenantProtection,
    },
    {
      title: 'benefits.magentaTv.headline',
      defaultTitle: 'MagentaTV Vorteil',
      type: Benefit.MAGENTA_TV,
      redirectTo: ROUTES.MAGENTA_TV,
    },
    {
      title: 'benefits.addressChange.headline',
      defaultTitle: 'Address Change',
      type: Benefit.ADDRESS_CHANGE,
      redirectTo: ROUTES.ADRESSWECHSEL,
    },
    {
      title: 'benefits.greenElectricity.headline',
      defaultTitle: 'Green Electricity',
      type: Benefit.GREEN_ELECTRICITY,
      redirectTo: ROUTES.GREEN_ELECTRICITY,
    },
  ],
  [BenefitsListPage.KauferPlus]: [
    {
      title: 'benefits.opportunityCheck.headline',
      defaultTitle: 'Opportunity check',
      type: Benefit.OPPORTUNITY_CHECK,
      redirectTo: ROUTES.CHANCEN_CHECK,
      isEntitledForService: (state) => state.hasKaeuferPlus,
    },
    {
      title: 'benefits.exclusiveListings.headline',
      defaultTitle: 'Exclusive listings',
      type: Benefit.EXCLUSIVE_LISTINGS,
      redirectTo: ROUTES.EXKLUSIVE_ANZEIGEN,
      isEntitledForService: (state) => state.hasKaeuferPlus,
    },
    {
      title: 'benefits.firstInInbox.headline',
      defaultTitle: 'First in inbox',
      type: Benefit.FIRST_IN_INBOX,
      redirectTo: ROUTES.OBEN_IM_POSTFACH,
      isEntitledForService: (state) => state.hasKaeuferPlus,
    },
    {
      title: 'benefits.schufaCheck.headline',
      defaultTitle: 'SCHUFA check',
      type: Benefit.SCHUFA_CHECK,
      redirectTo: ROUTES.SCHUFA,
    },
    {
      title: 'benefits.applicationPackage.headline',
      defaultTitle: 'Application package',
      type: Benefit.APPLICATION_PACKAGE,
      redirectTo: ROUTES.VORSTELLUNGSMAPPE,
    },
    {
      title: 'benefits.valuation.headline',
      defaultTitle: 'Real estate valuation',
      type: Benefit.VALUATION,
      redirectTo: ROUTES.IMMOBILIENBEWERTUNG,
    },
    {
      title: 'benefits.realEstateProof.headline',
      defaultTitle: 'DEKRA Real estate proof',
      type: Benefit.REAL_ESTATE_PROOF,
      redirectTo: ROUTES.DERKA_IMMOBILIENBEWERTUNG,
      shouldInclude: (state) => state.isRealEstateProofRequested,
    },
    {
      title: 'benefits.realEstateDamageReport.headline',
      defaultTitle: 'DEKRA Real estate damage report',
      type: Benefit.REAL_ESTATE_DAMAGE_REPORT,
      redirectTo: ROUTES.DEKRA_BAUSCHADENGUTACHTEN,
      shouldInclude: (state) => state.isRealEstateDamageReportRequested,
    },
    {
      title: 'benefits.legalInsurance.headline',
      defaultTitle: 'ARAG legal insurance',
      type: Benefit.LEGAL_INSURANCE,
      redirectTo: ROUTES.NACHBARSCHAFTS,
      shouldInclude: (state) => state.isKaeuferPlusLegalInsuranceAvailable,
    },
    {
      title: 'benefits.magentaTv.headline',
      defaultTitle: 'MagentaTV Vorteil',
      type: Benefit.MAGENTA_TV,
      redirectTo: ROUTES.MAGENTA_TV,
    },
    {
      title: 'benefits.addressChange.headline',
      defaultTitle: 'Address Change',
      type: Benefit.ADDRESS_CHANGE,
      redirectTo: ROUTES.ADRESSWECHSEL,
    },
  ],
};
